// src/components/Dialogs/DocumentViewerDialog.tsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Theme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SentimentDissatisfiedOutlinedIcon from "@mui/icons-material/SentimentDissatisfiedOutlined";

interface DocumentViewerDialogProps {
  viewerOpen: boolean;
  setViewerOpen: (open: boolean) => void;
  name: string | undefined;
  type: string | undefined;
  url: string | undefined;
  theme: Theme;
}

const getDocumentViewer = (
  theme: any,
  type: string | undefined,
  url: string | undefined,
) => {
  switch (type) {
    case "PDF":
    case "DOCX":
    case "DOC":
    case "XLSX":
    case "XLS":
      return (
        url && (
          <iframe
            src={`https://docs.google.com/gview?url=${encodeURIComponent(
              url,
            )}&embedded=true`}
            style={{ width: "100%", height: "70vh", border: "none" }}
            title="Document Viewer"
          />
        )
      );
    default:
      return (
        url && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <SentimentDissatisfiedOutlinedIcon
              style={{ color: theme.palette.text.secondary }}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="textSecondary" gutterBottom>
              ไม่สามารถแสดงผลไฟล์นี้ได้ กรุณาดาวน์โหลดไฟล์เพื่อเข้าถึงเอกสาร
            </Typography>
          </Box>
        )
      );
  }
};

const DocumentViewerDialog: React.FC<DocumentViewerDialogProps> = ({
  viewerOpen,
  setViewerOpen,
  name,
  type,
  url,
  theme,
}) => {
  return (
    <Dialog
      open={viewerOpen}
      onClose={() => setViewerOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.paper,
          background: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography variant="body1" style={{ fontWeight: 600 }}>
          Document Viewer
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {name}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => setViewerOpen(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {url && getDocumentViewer(theme, type, url)}
      </DialogContent>
      <DialogContent dividers sx={{ overflow: "none" }}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ flexWrap: "nowrap" }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 16, color: theme.palette.text.secondary }}
          />
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              flexGrow: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            เอกสารขนาดใหญ่จะใช้เวลาในการแสดงผล (กรณีเอกสารแสดงผลผิดพลาด
            กรุณาลองเปิดใหม่อีกครั้ง หรือเลือกดาวน์โหลด)
          </Typography>
          <Button
            variant="outlined"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ textAlign: "center", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            ดาวน์โหลด
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewerDialog;
